/**
 * **一本书的身份**：一个**具体的册** = `课本（出版社/名字）+ 年级 + 学期`（+ 学科）。
 *
 * ## 设计改过一次（2026-10-18，用户实测推翻 2026-10-16 的方案）
 *
 * 2026-10-16 的设计是 `课本 + 版次`（`edition` 必填且进唯一键），理由是
 * "只有版次能百分百确定书的内容变了"。用户录到一年级下册时发现这条路走不通：
 *
 * > "小学六年级每个年级上下册的语文书，如果同一个出版社，它的**出版号竟然都不一样**。
 * >  ……我输每一个年级、每个学期的三个表的时候，**其实属于同一个课本，但是因为版次
 * >  不一样，那就不会归到同一本书下面了**。……所以我觉得**这个版次是不需要的，
 * >  只需要出版社就可以了**，不然没办法录下去。"
 *
 * 但"只要出版社"会带来更糟的问题：`(publisher, subject)` 会把一年级到六年级、
 * 上下册**全部塌成一本**。用户真正要的是"**一册 = 一本书**"：
 *
 *   · 「人教版 · 小学一年级 · 上册」是一本；
 *   · 「人教版 · 小学一年级 · 下册」是**另一本**（实体书本来就是两本）；
 *   · 「人教版 · 小学二年级 · 上册」又是另一本。
 *
 * 所以身份是 **`publisher + subject + grade_code + semester`**（库里的
 * `uq_textbook_identity`，迁移 0015 改）。
 *
 * ## 版次（`edition`）：**彻底不用了**（2026-10-18 用户补充澄清）
 *
 * 用户原话："我**不是说只留出版社**这一个要素，我是说**要去掉版次，其他都保留**。"
 *
 * 所以：
 *   · 版次**不在身份里**，也**不在任何界面出现**、不参与任何显示；
 *   · 新建课本只有「出版社 / 课本名称（选填）/ 年级 / 学期」四格；
 *   · 导入文本里即使还有「版次」列，也是**读进来、忽略、不报错**
 *     （旧文本不能因为这一列突然报错）；
 *   · 数据库的 `edition` 列保留但可空 —— 只为不丢掉那 1 行历史数据，
 *     代码不再读写它。
 *
 * ⛔ **刻意不加 ISBN**：各册出版号都不一样，加它只会多一个填错的口子（用户否掉过）。
 *
 * ## 为什么放在 shared 而不是 api
 *
 * 界面上要**当场**告诉用户"这个册已经有一本了"，导入解析器要用**同一套**规范化
 * 判断"这几行说的是不是同一本"，服务层写库时也必须用同一套 —— 两份实现迟早漂移。
 *
 * ## 归一只做无歧义的改动
 *
 * 规范化只做这几件不会改变用户所写内容的事：
 *   1. 全角 → 半角（`２０２４` → `2024`，全角空格 → 半角）；
 *   2. 去掉零宽字符与 BOM；
 *   3. 连续空白折叠成一个空格、去首尾空白；
 *   4. 汉字与汉字**之间**的空格删掉（`2024 年 8 月` → `2024年8月`）。
 *
 * 年级 / 学期是**枚举 code**（`primary_1` / `上`），不做任何猜测；
 * 它们来自界面下拉或导入解析器的 `normalizeGrade` / `normalizeSemester`。
 */
import { gradeLabel } from '../constants.js';

/** 书名的最大长度（与 `textbook.publisher` / `textbook.name` 的 varchar(32/64) 对齐）。 */
export const TEXTBOOK_REF_MAX = 64;

// ---------------------------------------------------------------- 归一

/**
 * 课本文字归一（**只做无歧义的归一**，见文件头）。
 *
 * 用在"课本 / 出版社"那串字上（导入的「课本」列、界面里的出版社输入）。
 *
 * 例：
 *   `'  人民教育出版社 '`  → `'人民教育出版社'`
 *   `'人民教育　出版社'`    → `'人民教育 出版社'`（全角空格 → 半角）
 *   `'２０２４年版'`         → `'2024年版'`（**全角数字**变半角）
 *   `'人民教育  出版社'`    → `'人民教育 出版社'`（连续空白折叠成一个空格）
 *   `'人民教育 出版社'`     → `'人民教育出版社'`（汉字之间的排版空格删掉）
 *   `'人教版（修订）'`      → `'人教版（修订）'`（**标点原样保留**）
 *
 * ⚠️ **刻意不转全角标点**：那是"用户写的内容"，把「（修订）」折成「(修订)」
 *    属于擅自改写。所以这里只处理**空白与全角数字**。
 */
export function normalizeTextbookText(raw: string): string {
  return (
    raw
      // BOM / 零宽字符
      .replace(/[\uFEFF\u200B-\u200D]/g, '')
      // 全角数字 → 半角数字（只动数字：这是"看起来一样"里最典型的一种）
      .replace(/[\uFF10-\uFF19]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) - 0xfee0))
      // 全角空格 → 半角空格
      .replace(/\u3000/g, ' ')
      // 连续空白折叠成一个空格
      .replace(/\s+/g, ' ')
      .trim()
      /** 汉字与汉字之间的空格是**排版噪声**（"人民教育 出版社"），删掉。 */
      .replace(/(?<=[\u4e00-\u9fff]) +(?=[\u4e00-\u9fff])/g, '')
      // 汉字与数字之间的空格同理
      .replace(/(?<=[\u4e00-\u9fff]) +(?=[0-9])/g, '')
      .replace(/(?<=[0-9]) +(?=[\u4e00-\u9fff])/g, '')
  );
}

/**
 * 课本名用来**比较**的键（大小写不敏感 + 同一套归一）。
 *
 * 与数据库里的 `lower(publisher) = lower(...)` 同口径 —— 也就是说
 * "RenJiao" 与 "renjiao" 是同一本书，不会因为大小写多出一本。
 */
export function textbookRefKey(raw: string | null | undefined): string {
  return normalizeTextbookText(raw ?? '').replace(/\s+/g, '').toLowerCase();
}

/** 学期在**书名**里的写法：「上册 / 下册 / 不分」。 */
export function semesterTitle(semester: string | null | undefined): string {
  if (semester === '上') return '上册';
  if (semester === '下') return '下册';
  if (semester === '不分') return '不分';
  return (semester ?? '').trim();
}

// ---------------------------------------------------------------- 身份

/** 一本书的**自然键**（= 库里的 `uq_textbook_identity`）。 */
export interface TextbookIdentity {
  /**
   * 课本（用户在「课本」列里写的那串字面：`人民教育出版社` / `人教版`）。
   *
   * ⚠️ 库里它同时写进 `publisher` 与 `name` 两列（导入没有单独的"出版社"字段）——
   *    这是**既有事实**，本轮不变。
   */
  textbook: string;
  /** 年级 code（`primary_1` …）。**必填**。 */
  gradeCode: string;
  /** 学期（`上` / `下` / `不分`）。**必填**。 */
  semester: string;
  /** 学科，默认 `chinese`。 */
  subject?: string;
}

/** 归一后的身份（字段都已归一；年级/学期原样是 code）。 */
export interface NormalizedTextbookIdentity {
  textbook: string;
  gradeCode: string;
  semester: string;
  subject: string;
}

/**
 * 归一一条身份。
 *
 * ⚠️ **不校验**年级/学期是否为空（那是调用方的事：解析器要报逐行错误、
 *    服务层要报 400、界面要在按钮上拦）。这里只负责把值变成可以比较的形状。
 */
export function normalizeTextbookIdentity(input: TextbookIdentity): NormalizedTextbookIdentity {
  return {
    textbook: normalizeTextbookText(input.textbook),
    gradeCode: input.gradeCode.trim(),
    semester: input.semester.trim(),
    subject: (input.subject ?? 'chinese').trim().toLowerCase() || 'chinese',
  };
}

/**
 * 身份的比较键 —— **这就是"两本书是不是同一本"的唯一判据**。
 *
 * 同一册（同课本 + 同年级 + 同学期）必然得到同一个键；换了年级或学期必然不同。
 * **没有版次**：版次已经彻底从身份里拿掉（2026-10-18）。
 *
 * 用 `\u0000` 分隔四个字段：它不可能出现在用户输入里，所以不会出现
 * `("ab", "c")` 与 `("a", "bc")` 撞键这种经典拼接错误。
 */
export function textbookIdentityKey(input: TextbookIdentity): string {
  const id = normalizeTextbookIdentity(input);
  return [
    textbookRefKey(id.textbook),
    id.gradeCode,
    id.semester,
    id.subject,
  ].join('\u0000');
}

/**
 * 两个身份是不是同一本书（键相等）。
 *
 * 解析器用它做"批内重复课本"的判定，服务层用它做"是并入已有那本、还是新建一本"的判定。
 */
export function isSameTextbook(a: TextbookIdentity, b: TextbookIdentity): boolean {
  return textbookIdentityKey(a) === textbookIdentityKey(b);
}

// ---------------------------------------------------------------- 展示

/**
 * 一本书在**下拉 / 列表**里怎么显示：`人民教育出版社 · 小学一年级 · 上册`。
 *
 * ⚠️ 年级与学期**必须**出现在这里 —— 下册与上册是两本书，下拉里只显示出版社时
 *    两个选项长得一模一样，选错就是"把下册的字录进上册"。
 * ⛔ **不显示版次**：版次已经彻底不用了（2026-10-18），下拉里不该再出现它。
 */
export function describeTextbookLabel(input: {
  name: string;
  gradeCode: string;
  semester: string;
  isDefault?: boolean;
}): string {
  const name = input.name.trim();
  const grade = gradeLabel(input.gradeCode);
  const semester = semesterTitle(input.semester);
  const parts = [name, grade, semester].filter((part) => part !== '');
  const core = parts.join(' · ');
  return input.isDefault === true ? `${core}（默认）` : core;
}

/** 年级 + 学期的短提示（新建课本表单里那行小字）。 */
export const VOLUME_HINT =
  '必填：课本按「册」建 —— 一年级上册和一年级下册是两本书，要分别建。选哪一册，字表就录进哪一册。';
